import type { EditorHandle } from "@codeshare/editor";
import type { TestResult } from "@codeshare/runner-core";
import { findRunnables, isTestFile } from "./code-lens";

type Monaco = EditorHandle["monaco"];
type Model = ReturnType<EditorHandle["editor"]["getModel"]> & object;

const LANGUAGES = ["go", "python", "javascript", "typescript", "ruby", "ocaml"];
const pathOf = (model: Model) => model.uri.path.replace(/^\//, "");
const short = (text: string, max = 90) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

function summary(result: TestResult): string {
  if (result.status === "pass")
    return `$(pass) Passed${result.durationMs ? ` · ${result.durationMs} ms` : ""}`;
  if (result.status === "skip") return "$(debug-step-over) Skipped";
  return `$(error) ${short(result.failure?.message.split("\n")[0] ?? "Failed")}`;
}

/**
 * Run and test links in the editor, like VS Code's code lenses (▷ Run above main, ▷ Run test
 * above each test), and the latest results inline: ✓/✗ in the margin by each test, and the
 * failure message at the failing line.
 */
export function installEditorRuns(
  handle: EditorHandle,
  actions: { run: (path: string) => void; test: (path: string, name?: string) => void },
) {
  const { monaco, editor } = handle;
  let results: readonly TestResult[] = [];
  // CodeLens providers refresh when this fires.
  const changed = new monaco.Emitter<never>();
  const disposables = [
    monaco.editor.registerCommand("codescreen.run", (_: unknown, path: string) =>
      actions.run(path),
    ),
    monaco.editor.registerCommand("codescreen.test", (_: unknown, path: string, name?: string) =>
      actions.test(path, name),
    ),
    ...LANGUAGES.map((language) =>
      monaco.languages.registerCodeLensProvider(language, {
        onDidChange: changed.event as never,
        provideCodeLenses(model) {
          const path = pathOf(model);
          const languageId = model.getLanguageId();
          const runnables = findRunnables(languageId, path, model.getValue());
          const at = (line: number) => new monaco.Range(line, 1, line, 1);
          const lenses: {
            range: InstanceType<Monaco["Range"]>;
            command: { id: string; title: string; arguments?: unknown[] };
          }[] = [];
          const tests = runnables.filter((r) => r.kind === "test");
          if (isTestFile(languageId, path, model.getValue()) && tests.length > 1) {
            const failed = results.filter((r) => r.file === path && r.status === "fail").length;
            const passed = results.filter((r) => r.file === path && r.status === "pass").length;
            lenses.push({
              range: at(1),
              command: {
                id: "codescreen.test",
                title: `$(run-all) Run all tests${failed || passed ? ` · ${passed} passed${failed ? `, ${failed} failed` : ""}` : ""}`,
                arguments: [path],
              },
            });
          }
          for (const runnable of runnables) {
            if (runnable.kind === "main") {
              lenses.push({
                range: at(runnable.line),
                command: { id: "codescreen.run", title: "$(play) Run", arguments: [path] },
              });
              continue;
            }
            // An unnamed test entry runs the file's tests (an Alcotest executable's suites).
            lenses.push({
              range: at(runnable.line),
              command: {
                id: "codescreen.test",
                title: runnable.name ? "$(play) Run test" : "$(run-all) Run tests",
                arguments: runnable.name ? [path, runnable.name] : [path],
              },
            });
            const result = results.find((r) => r.file === path && r.name === runnable.name);
            if (result)
              lenses.push({
                range: at(runnable.line),
                command: { id: "", title: summary(result) },
              });
          }
          return { lenses, dispose() {} };
        },
      }),
    ),
  ];

  // ✓/✗ in the margin, and failure messages at the failing lines, for the open file.
  const decorations = editor.createDecorationsCollection();
  const decorate = () => {
    const model = editor.getModel();
    if (!model) return;
    const path = pathOf(model);
    const lines = model.getLineCount();
    const valid = (line?: number) => (line && line >= 1 && line <= lines ? line : undefined);
    const items: Parameters<typeof decorations.set>[0][number][] = [];
    for (const result of results) {
      const line = result.file === path ? valid(result.line) : undefined;
      if (line) {
        items.push({
          range: new monaco.Range(line, 1, line, 1),
          options: {
            glyphMarginClassName: `test-glyph test-${result.status}`,
            glyphMarginHoverMessage: {
              value: `${result.name}: ${result.status === "fail" ? (result.failure?.message ?? "failed") : result.status === "pass" ? "passed" : "skipped"}`,
            },
          },
        });
      }
      const failureLine =
        result.status === "fail" && result.failure?.file === path
          ? valid(result.failure.line)
          : undefined;
      if (failureLine) {
        items.push({
          range: new monaco.Range(failureLine, 1, failureLine, model.getLineMaxColumn(failureLine)),
          options: {
            isWholeLine: true,
            className: "test-failure-line",
            after: {
              // The first line: details (a diff, a backtrace) are in the margin's hover.
              content: `    ✗ ${short(result.failure?.message.split("\n")[0] ?? "failed", 120)}`,
              inlineClassName: "test-failure-inline",
            },
          },
        });
      }
    }
    decorations.set(items);
  };
  disposables.push(editor.onDidChangeModel(decorate));

  return {
    /** New results (e.g. as tests finish): refresh the lenses and the inline marks. */
    setResults(next: readonly TestResult[]) {
      results = next;
      changed.fire(undefined as never);
      decorate();
    },
    dispose() {
      decorations.clear();
      for (const disposable of disposables) disposable.dispose();
      changed.dispose();
    },
  };
}
